import type { ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'

export type GameErrorKind = 'notFound' | 'error'

export interface GameErrorCard {
  title: string
  effectLabel: string
  effect: string
  flavor: string
  /** Texto del reverso (marca). */
  back: string
}

export interface GameErrorPageProps {
  kind: GameErrorKind
  eyebrow: string
  /** Titular (puede traer un `<span>` resaltado vía `<Trans>`). */
  title: ReactNode
  lead: string
  card: GameErrorCard
  /** Acciones (enlaces/botones ya montados por el caller: router o `<a>` según el contexto). */
  actions: ReactNode
  /** Pantalla completa (boundaries, fuera del layout) o dentro del contenido de la página. */
  fullScreen?: boolean
}

const TONE: Record<GameErrorKind, { dot: string; band: string; symbol: string; code: string }> = {
  notFound: { dot: 'bg-green', band: 'bg-green', symbol: '?', code: '404' },
  error: { dot: 'bg-red', band: 'bg-red', symbol: '!', code: 'ERROR' },
}

/**
 * Página de error con la carta «perdida» (404) o «de evento imprevisto» (error general).
 * Diseño elegido el 4-oct-2026 (propuesta C). Puramente de presentación: los textos llegan
 * por props, así la usa también el boundary global, que vive fuera del proveedor de i18n.
 * La versión sin JS (404 servido por la Pages Function) inyecta los mismos textos.
 */
export function GameErrorPage({
  kind,
  eyebrow,
  title,
  lead,
  card,
  actions,
  fullScreen = false,
}: GameErrorPageProps) {
  const tone = TONE[kind]
  return (
    <section
      className={cn(
        'mx-auto grid max-w-6xl items-center gap-5 px-4 md:grid-cols-[1.05fr_1fr] md:gap-8 md:px-8',
        fullScreen ? 'min-h-dvh bg-background py-8' : 'py-4 md:py-20',
      )}
    >
      <div className="order-2 text-center md:order-1 md:text-left">
        <p className="chip inline-flex items-center gap-2 rounded-full bg-background px-3 py-1 font-brutal text-[11px] font-bold uppercase tracking-widest">
          <span aria-hidden="true" className={cn('size-2 rounded-full', tone.dot)} />
          {eyebrow}
        </p>
        <h1 className="mt-4 font-display text-[2.5rem] font-black leading-[0.95] tracking-tight md:text-6xl">
          {title}
        </h1>
        <p className="mx-auto mt-4 max-w-md text-base font-medium text-foreground/80 md:mx-0 md:text-lg">
          {lead}
        </p>
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row md:justify-start">
          {actions}
        </div>
      </div>

      <LostCard kind={kind} card={card} className="order-1 md:order-2" />
    </section>
  )
}

/** Resaltado amarillo del titular (lo usan los `<Trans components={{ hl: … }}>`). */
export function ErrorTitleHighlight({ children }: { children?: ReactNode }) {
  return (
    <span className="brutal-sm inline-block rotate-[-1.5deg] rounded-md bg-yellow px-2">
      {children}
    </span>
  )
}

function LostCard({
  kind,
  card,
  className,
}: {
  kind: GameErrorKind
  card: GameErrorCard
  className?: string
}) {
  const tone = TONE[kind]
  return (
    <div
      aria-hidden="true"
      className={cn('relative mx-auto h-[250px] w-[260px] md:h-[380px] md:w-[340px]', className)}
    >
      {/* Reverso */}
      <div className="brutal absolute left-0 top-6 flex h-[200px] w-[145px] -rotate-12 items-center justify-center rounded-2xl bg-blue pr-7 md:h-[300px] md:w-[210px] md:pr-16">
        <div className="absolute inset-3 rounded-xl border-[3px] border-dashed border-white/80" />
        <span className="font-display text-2xl font-black text-white md:text-4xl">{card.back}</span>
      </div>
      {/* Anverso */}
      <div className="brutal absolute right-0 top-0 flex h-[240px] w-[165px] rotate-6 animate-float flex-col gap-2 rounded-2xl bg-background p-2.5 md:h-[320px] md:w-[220px]">
        <div
          className={cn(
            'rounded-lg border-[3px] border-foreground px-2 py-1.5 text-center font-display text-sm font-black text-white md:text-base',
            tone.band,
          )}
        >
          {card.title}
        </div>
        <div className="relative flex flex-1 items-center justify-center rounded-lg border-[3px] border-foreground bg-yellow-soft">
          <span className="font-display text-5xl font-black md:text-7xl">{tone.symbol}</span>
          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-foreground px-3 py-0.5 font-brutal text-[10px] font-bold text-yellow">
            {tone.code}
          </span>
        </div>
        <div className="mt-1 rounded-lg border-2 border-foreground bg-white px-2 py-1.5 font-brutal text-[10px] leading-snug md:text-[11px]">
          <p className="font-bold uppercase">{card.effectLabel}:</p>
          <p>{card.effect}</p>
          {/* En móvil no cabe con aire: se deja el efecto, que es lo útil. */}
          <p className="mt-1 hidden text-foreground/60 md:block">{card.flavor}</p>
        </div>
      </div>
    </div>
  )
}
